import Image from 'next/image';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button, IconPopover, StatusBadge, Text } from '@/components/atoms/';
import MoreIcon from '@/icons/more.svg';
import {
  useGetMerchantSkusQuery,
  useUpdateMerchantSkuDetailMutation,
} from '@/query/service/inventoryService';
import { ResponseError } from '@/query/types/response';
import {
  closeBottomDrawer,
  closePopup,
  openDrawer,
  openPopup,
} from '@/redux-toolkit/app/slice';
import { activeUserState } from '@/redux-toolkit/auth/auth.slice';
import { useAppDispatch, useAppSelector } from '@/redux-toolkit/hooks';
import { setSelectedMerchantSku } from '@/redux-toolkit/products/products.slice';
import { EnumTableParam } from '@/types/app';
import { EnumDrawerType, EnumRole, EnumStatus } from '@/types/constants';
import { EnumProductTableParam } from '@/types/product';
import toRupiah from '@/utils/angka-rupiah';
import { toast } from '@/utils/toast';

import usePaginationAndSearch from '../usePaginationAndSearch';

const statusHandler = (value: boolean) => (
  <StatusBadge status={value ? EnumStatus.ACTIVE : EnumStatus.INACTIVE} />
);

const usePrepareProductVendorTable = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const userState = useAppSelector(activeUserState);
  const [updateMerchantSkuDetail] = useUpdateMerchantSkuDetailMutation();

  const { getQuery } = usePaginationAndSearch();
  const { data: merchantSkusResponse } = useGetMerchantSkusQuery(
    {
      page: getQuery<number>(EnumProductTableParam.PAGE, 1),
      size: getQuery<number>(EnumProductTableParam.PAGE_SIZE, 10),
      merchantCode: userState?.code ?? '',
      product_name: getQuery<string | undefined>(
        EnumTableParam.KEYWORD,
        undefined
      ),
      order: getQuery<string | undefined>(
        EnumProductTableParam.ORDER,
        undefined
      ),
      sort: getQuery<string | undefined>(EnumProductTableParam.SORT, undefined),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: userState?.role === EnumRole.STORE || !userState?.code,
    }
  );

  const handleActivateMutation = async (
    skuId: number,
    value: boolean,
    price: number
  ) => {
    try {
      if (price !== 0) {
        await updateMerchantSkuDetail({
          merchantCode: userState?.code as string,
          id: skuId,
          is_active: value,
        }).unwrap();
        toast.success(`Your changes is saved!`, {
          autoClose: 1000,
        });
      } else {
        toast.error('Please input price before activate a product');
      }
    } catch (error) {
      const { data } = error as ResponseError;
      toast.error(
        data?.errors?.[0]?.message || formatMessage({ id: 'error_msg' })
      );
    }

    dispatch(closePopup());
  };

  const { dataDesktop, dataMobile } = useMemo(() => {
    const handleActivateProduct = (
      isActive: boolean,
      skuId: number,
      price: number
    ) => {
      dispatch(
        openPopup({
          isOpen: true,
          isGlobal: true,
          message: `Are you sure want to ${
            isActive ? 'deactivate' : 'activate'
          } this product?`,
          title: `${isActive ? 'Deactivate' : 'Activate'} Product`,
          actions: [
            {
              variant: 'white',
              action: () => {
                dispatch(closePopup());
              },
              label: 'Cancel',
            },
            {
              variant: 'solid',
              action: () => {
                handleActivateMutation(skuId, !isActive, price);
              },
              label: 'Confirm',
            },
          ],
        })
      );
    };

    const handleOpenDrawer = (type: EnumDrawerType, title: string) => {
      dispatch(
        openDrawer({
          isOpen: true,
          title: title,
          type: type,
        })
      );
    };

    const handleSelectedMerchantSku = (skuCode: string) => {
      dispatch(
        setSelectedMerchantSku({
          skuCode,
        })
      );
    };

    const handleOpenDetail = () => {
      dispatch(
        openDrawer({
          isOpen: true,
          title: 'product_details',
          type: EnumDrawerType.VIEW,
        })
      );
    };

    const handleEdit = (skuCode: string) => {
      handleOpenDrawer(EnumDrawerType.EDIT, 'edit_product');
      handleSelectedMerchantSku(skuCode);
    };

    const dataDesktop =
      merchantSkusResponse?.data.map((merchantSku) => {
        const { merchant_sku, sku } = merchantSku;
        const { price, is_active: isSkuActive } = merchant_sku;
        const selling_unit = `${sku?.selling_amount} ${sku?.selling_amount_uom.name} @ ${sku?.lowest_amount} ${sku?.lowest_unit_uom.name}`;

        const dropdownActions = [
          {
            key: 'view',
            label: 'View Product',
            action: () => {
              handleOpenDrawer(EnumDrawerType.VIEW, 'product_details');
              handleSelectedMerchantSku(merchant_sku.code);
            },
          },
          {
            key: 'edit',
            label: formatMessage({ id: 'edit_product' }),
            action: () => handleEdit(merchant_sku.code),
          },
          {
            key: 'is_active',
            label: isSkuActive
              ? formatMessage({ id: 'deactivate' })
              : formatMessage({ id: 'activate' }),
            action: () =>
              handleActivateProduct(isSkuActive, merchant_sku.id, price),
          },
        ];

        return {
          ...merchantSku,
          code: merchantSku.code,
          price: toRupiah(price),
          selling_unit,
          status: statusHandler(isSkuActive),
          action: dropdownActions && (
            <IconPopover
              icon={
                <div className="bg-primary-light rounded-lg cursor-pointer flex items-center justify-center py-0.5 px-1">
                  <Image alt="" height={24} src={MoreIcon.src} width={24} />
                </div>
              }
              popoverContent={dropdownActions.map((dropdownItem, index) => (
                <div
                  key={index}
                  className="flex items-center p-2 z-50 cursor-pointer w-full"
                  onClick={dropdownItem.action as () => void}
                >
                  <Text
                    className="text-md font-normal
                  "
                    label={dropdownItem.label}
                  />
                </div>
              ))}
            />
          ),
        };
      }) || [];

    const dataMobile =
      merchantSkusResponse?.data.map((merchantSku) => {
        const { merchant_sku, sku } = merchantSku;
        const { price, is_active: isSkuActive } = merchant_sku;
        const selling_unit = `${sku?.selling_amount} ${sku?.selling_amount_uom.name} @ ${sku?.lowest_amount} ${sku?.lowest_unit_uom.name}`;
        let dropdownActions;
        if (userState?.role === EnumRole.VENDOR) {
          dropdownActions = [
            {
              key: 'view',
              label: 'View Product',
              action: () => {
                handleOpenDrawer(EnumDrawerType.VIEW, 'product_details');
                handleSelectedMerchantSku(merchant_sku.code);
              },
            },
            {
              key: 'edit',
              label: formatMessage({ id: 'edit_product' }),
              action: () => {
                handleEdit(merchant_sku.code);
              },
            },
            {
              key: 'is_active',
              label: isSkuActive
                ? formatMessage({ id: 'deactivate' })
                : formatMessage({ id: 'activate' }),
              action: () =>
                handleActivateProduct(isSkuActive, merchant_sku.id, price),
            },
          ];
        } else if (userState?.role === EnumRole.STORE) {
          dropdownActions = [
            {
              key: 'view',
              label: 'View Product',
              action: () => {
                handleOpenDetail();
              },
            },
            {
              key: 'is_active',
              label: isSkuActive
                ? formatMessage({ id: 'deactivate' })
                : formatMessage({ id: 'activate' }),
              action: () =>
                handleActivateProduct(isSkuActive, merchant_sku.id, price),
            },
          ];
        }

        return {
          ...merchantSku,
          code: merchantSku.code,
          price: toRupiah(price),
          selling_unit,
          status: statusHandler(isSkuActive),
          action: dropdownActions && (
            <>
              {dropdownActions.map((action) => {
                return (
                  <Button
                    key={action.key}
                    className="text-black w-full justify-center flex flex-col items-start"
                    onClick={() => {
                      action.action();
                      dispatch(closeBottomDrawer());
                    }}
                    variant="unstyled"
                  >
                    <Text
                      className="text-start"
                      typography="content-label-large"
                    >
                      {action.label}
                    </Text>
                  </Button>
                );
              })}
            </>
          ),
        };
      }) || [];
    return { dataDesktop, dataMobile };
  }, [dispatch, formatMessage, merchantSkusResponse, userState]);

  const totalCount = merchantSkusResponse?.meta.total_count ?? 0;
  const pageSize = merchantSkusResponse?.meta.size ?? 0;

  return {
    dataDesktop,
    dataMobile,
    totalCount,
    pageSize,
  };
};

export default usePrepareProductVendorTable;
